import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
    ValidationService,
    ValidationStatus,
    TemplateCriteriaSectionName,
} from '@r1-compass-library/features/validation-sync';
import { DataMartField, GenericSelectionCriterionData, TemplateCriteria } from '@r1-compass-library/models';
import {
    isGenericForm,
    TemplateCriteriaBuilderForm,
    TemplateCriteriaDefinitionForm,
} from '@r1-compass-library/models/template-criteria/template-criteria-form.model';
import { lookupComparators } from '@r1-compass-library/models/content-library/content-library.model';
import { typeValidator } from '@r1-compass-library/validators';
import { DisableValue1AndValue2Conditionally } from '@r1-compass-library/features/validation-sync/disable-value1-and-value2-conditionally.service';

export type TemplateCriteriaValidationStatus = {
    erroredSections: Set<TemplateCriteriaSectionName>;
};

export type TemplateCriteriaFormMap = {
    definition: FormGroup<TemplateCriteriaDefinitionForm>;
    recordSelection: FormGroup<TemplateCriteriaBuilderForm>;
};

@Injectable({
    providedIn: 'root',
})
export class TemplateCriteriaValidationService
    implements ValidationService<TemplateCriteriaSectionName, TemplateCriteria, TemplateCriteriaFormMap>
{
    public validateSection<K extends TemplateCriteriaSectionName>(
        form: TemplateCriteriaFormMap[K],
        section: K
    ): ValidationStatus<TemplateCriteriaSectionName> {
        const validationStatus: TemplateCriteriaValidationStatus = { erroredSections: new Set() };
        if (form.invalid) {
            validationStatus.erroredSections.add(section);
        }
        return validationStatus;
    }

    public validateModel(model: TemplateCriteria): ValidationStatus<TemplateCriteriaSectionName> {
        const validationStatus: TemplateCriteriaValidationStatus = { erroredSections: new Set() };

        const definitionForm = new FormGroup<TemplateCriteriaDefinitionForm>(new TemplateCriteriaDefinitionForm(model));
        if (definitionForm.invalid) {
            validationStatus.erroredSections.add('definition');
        }

        const builderForm = new FormGroup(new TemplateCriteriaBuilderForm(model));

        const usedDatamartFields = model.config.selectionCriteria
            ?.filter(c => c.type === 'generic' || c.type === 'model')
            .map(c => (c.data as GenericSelectionCriterionData).dataMartField)
            .filter(field => !!field);

        DisableValue1AndValue2Conditionally.disableTemplateCriteria(builderForm, usedDatamartFields);
        this.setValidatorsForInputTypes(builderForm, usedDatamartFields);

        if (builderForm.invalid) {
            validationStatus.erroredSections.add('recordSelection');
        }

        return validationStatus;
    }

    /**
     * Sets input type validators (ex: numeric, data) on the formcontrols Value1 and Value2
     */
    private setValidatorsForInputTypes(
        builderForm: FormGroup<TemplateCriteriaBuilderForm>,
        usedDataMartFields: DataMartField[] | undefined
    ) {
        if (!usedDataMartFields || usedDataMartFields.length === 0) return;

        builderForm.controls.selectionCriteria.controls.forEach(c => {
            if (isGenericForm(c.controls.data)) {
                const typedForm = c.controls.data;
                const dataMartFieldName = typedForm.controls.dataMartFieldName.value;
                const dataMartField = usedDataMartFields.find(field => field.name === dataMartFieldName);

                if (dataMartField) {
                    const comparator = typedForm.controls.comparator.value;
                    const comparatorType = lookupComparators(dataMartField).find(
                        comparatorMetadata => comparatorMetadata.value === comparator
                    )?.valueType;

                    const dataTypeIsNumeric = ['integer', 'number', 'numeric'].includes(
                        dataMartField.dataType.toLowerCase()
                    );

                    if (comparatorType === 'date') {
                        typedForm.controls.value1.controls.value.addValidators(typeValidator('date'));
                    } else if (comparatorType === 'timeInterval') {
                        typedForm.controls.value1.controls.value.addValidators(typeValidator('numeric'));
                    } else if (comparatorType === 'date&date') {
                        typedForm.controls.value1.controls.value.addValidators(typeValidator('date'));
                        typedForm.controls.value2.controls.value.addValidators(typeValidator('date'));
                    } else if (comparatorType === 'text' && dataTypeIsNumeric) {
                        typedForm.controls.value1.controls.value.addValidators(typeValidator('numeric'));
                    } else if (comparatorType === 'text&text' && dataTypeIsNumeric) {
                        typedForm.controls.value1.controls.value.addValidators(typeValidator('numeric'));
                        typedForm.controls.value2.controls.value.addValidators(typeValidator('numeric'));
                    }
                    typedForm.controls.value1.controls.value.updateValueAndValidity();
                    typedForm.controls.value2.controls.value.updateValueAndValidity();
                }
            }
        });
    }
}
